import type { CampaignEditorTab } from './campaignEditorNavigation'

/** Ajuda contextual: orienta o preenchimento sem acrescentar regras de validação. */
export const campaignEditorHelp: Record<
  CampaignEditorTab,
  { requirement: string; tips: string[] }
> = {
  'roleta-api': {
    requirement: 'Configure fatias, origens autorizadas e horários vencedores.',
    tips: [
      'Cada intervalo tem um único ganhador e termina no próximo horário ou na virada do dia.',
      'A chave de integração pertence somente ao servidor externo.',
      'O log geral inclui autorizações não utilizadas; cada horário tem seu próprio histórico de giros.',
    ],
  },
  'inteligencia-artificial': {
    requirement: 'Defina o preço e apresente sua entidade ao assistente.',
    tips: ['O valor por crédito deve respeitar o mínimo da plataforma.', 'Inclua informações públicas e atuais sobre a entidade e a campanha.', 'Cada resposta concluída consome um crédito. Falhas devolvem o crédito.'],
  },
  'raspadinha-compra': {
    requirement: 'As três imagens são obrigatórias para ativar. Cada compra paga libera uma tentativa.',
    tips: ['Use três imagens com a mesma largura e altura.', 'Arraste sobre a capa ou ajuste as coordenadas para definir a área raspável.', 'A soma das probabilidades deve ser 100%. As tentativas já abertas preservam sua configuração.'],
  },
  identidade: {
    requirement: 'Nome obrigatório',
    tips: [
      'Use um nome fácil de reconhecer no painel. O título que o visitante vê é editado em Página pública → Conteúdo.',
      'Mantenha o status Rascunho enquanto prepara a campanha. Revise a página antes de ativá-la.',
      'Na descrição do prêmio, informe o que será entregue e as condições que o participante precisa conhecer.',
    ],
  },
  'periodo-valores': {
    requirement: 'Confira antes de salvar',
    tips: [
      'Defina o início e o encerramento com data e hora. O encerramento deve acontecer depois do início.',
      'O preço é por número. Os limites mínimo e máximo se aplicam a cada compra.',
      'A quantidade total de números ou a opção Compra sem números da sorte fica bloqueada após a primeira definição.',
    ],
  },
  'certificados-regulamento': {
    requirement: 'Documentos da campanha',
    tips: [
      'Escolha como apresentar o regulamento: envie um PDF ou escreva as regras no editor.',
      'Confira o documento antes de salvar. O formato selecionado define o regulamento exibido na página.',
      'Preencha o certificado e seu número quando eles fizerem parte da campanha.',
    ],
  },
  template: {
    requirement: 'Um modelo por campanha',
    tips: [
      'Escolha a apresentação mais adequada às imagens e aos textos que você possui.',
      'O Template 3 precisa de conteúdo próprio. Depois de escolhê-lo, complete a aba Seções do Template 3.',
      'Ao trocar o modelo, os dados dos outros modelos são preservados.',
    ],
  },
  'pagina-publica': {
    requirement: 'Conteúdo complementar',
    tips: [
      'Comece com um título direto que apresente a campanha ou o prêmio.',
      'Use parágrafos curtos. A barra do editor permite destacar trechos e organizar listas.',
      'Use Visualizar Página para conferir o resultado depois de salvar.',
    ],
  },
  'conteudo-template': {
    requirement: 'Campos obrigatórios neste modelo',
    tips: [
      'Complete os textos e imagens de cada seção. O indicador de preenchimento mostra o que falta.',
      'Você pode recolher as seções já revisadas para se concentrar na próxima.',
      'As dimensões sugeridas são orientações. A prévia mostra a imagem inteira para facilitar a revisão.',
    ],
  },
  'comunicacao-midia': {
    requirement: 'Identidade visual',
    tips: [
      'Envie uma imagem nítida, com o prêmio ou a causa em destaque. Evite informações pequenas dentro da arte.',
      'Arraste uma imagem para o campo ou use Escolher imagem. Ampliar abre a prévia para revisão.',
      'Na galeria, use as setas para definir a ordem. As novas imagens e a nova ordem são aplicadas ao salvar.',
      'Escolha cores que mantenham os textos e os botões fáceis de ler.',
    ],
  },
  links: {
    requirement: 'Preenchimento opcional',
    tips: [
      'O WhatsApp da campanha vale para o botão flutuante e para o contato do rodapé.',
      'Preciso de Ajuda cria um link com número e mensagem inicial. Teste antes de salvar.',
      'Nas redes sociais, cole o endereço completo do perfil. Deixe vazias as redes que não utiliza.',
    ],
  },
  'dominio-personalizado': {
    requirement: 'Atenção ao endereço público',
    tips: [
      'O endereço curto identifica a página. Alterá-lo pode afetar links já compartilhados.',
      'O domínio próprio é opcional e precisa da configuração de DNS indicada nesta aba.',
      'Preencha o site externo somente se quiser encaminhar os visitantes para outra página.',
    ],
  },
  combos: {
    requirement: 'Recurso opcional',
    tips: [
      'Crie opções de quantidade e preço fáceis de comparar para o participante.',
      'Confira o status de cada combo para definir quais ofertas aparecem na campanha.',
      'Use o botão do formulário do combo para salvar a oferta.',
    ],
  },
  'numeros-premiados': {
    requirement: 'Recurso opcional',
    tips: [
      'Informe o número e descreva claramente o prêmio associado a ele.',
      'Revise os números e as descrições antes de usar Salvar campanha.',
    ],
  },
  roleta: {
    requirement: 'Recurso opcional',
    tips: [
      'Configure os prêmios e as condições de participação antes de disponibilizar a roleta.',
      'Use as ações de cada configuração para salvar suas alterações.',
    ],
  },
  avatar: {
    requirement: 'Opcional. Publique peças para disponibilizá-las nesta campanha.',
    tips: [
      'Crie peças a partir de uma base ou envie uma arte própria.',
      'Combine imagens, símbolos e textos em até 8 camadas.',
      'Arquivar oculta a peça para novas escolhas e preserva os avatares já salvos.',
    ],
  },
  gamificacao: {
    requirement: 'Títulos dos selos obrigatórios',
    tips: [
      'Cada selo corresponde a uma conquista do participante. Personalize a mensagem para esse momento.',
      'O título é obrigatório. A mensagem e o vídeo são opcionais.',
      'As mensagens podem ser salvas pelo botão da seção ou por Salvar campanha.',
    ],
  },
  pergunta: {
    requirement: 'Recurso opcional',
    tips: [
      'Faça perguntas curtas e use alternativas que o participante consiga distinguir.',
      'Salve cada pergunta no seu próprio formulário.',
    ],
  },
  widget: {
    requirement: 'Para usar em outro site',
    tips: [
      'Personalize o botão de compra e confira sua aparência na prévia.',
      'Salve a campanha antes de copiar o código para o site onde ele será utilizado.',
    ],
  },
  compartilhamento: {
    requirement: 'Preenchimento opcional',
    tips: [
      'O título e a descrição ajudam a apresentar o link no WhatsApp e nas redes sociais.',
      'Se não enviar uma imagem de compartilhamento, será usada a imagem principal da campanha.',
      'As redes sociais podem levar algum tempo para atualizar a prévia de um link já compartilhado.',
    ],
  },
  email: {
    requirement: 'Personalização opcional',
    tips: [
      'Deixe assunto e mensagem vazios para usar o e-mail padrão do sistema.',
      'Os botões de dados do participante inserem informações como nome, campanha e números automaticamente.',
      'Usar exemplo substitui o assunto e a mensagem atuais. Revise o texto antes de salvar.',
    ],
  },
  pixels: {
    requirement: 'Integrações opcionais',
    tips: [
      'Preencha somente as plataformas que você usa para acompanhar seus anúncios.',
      'Copie os identificadores do Meta ou do Google e confira os exemplos de formato.',
      'O envio de compras confirmadas exige os dados adicionais indicados em cada plataforma.',
    ],
  },
  dashboard: { requirement: 'Acompanhamento', tips: [] },
}
